import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { Award, BookOpen, Check, ChevronDown, Clock, Globe, Heart, Play, Star, Users, Zap } from "lucide-react";

const ICONS = {
  check: Check,
  star: Star,
  play: Play,
  book: BookOpen,
  clock: Clock,
  award: Award,
  users: Users,
  globe: Globe,
  zap: Zap,
  heart: Heart,
};

function Stars({ value }) {
  const n = Number(value) || 0;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${n} stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-white/30"}`} />
      ))}
    </span>
  );
}

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function VideoPreview({ page }) {
  const [play, setPlay] = useState(false);
  if (!page.video_kind) {
    return (
      <div className="aspect-video bg-[#1c1d1f] text-white flex items-center justify-center text-sm">
        Preview this course
      </div>
    );
  }
  if (!play) {
    return (
      <button type="button" className="relative block w-full aspect-video bg-[#1c1d1f] text-white" onClick={() => setPlay(true)} data-testid="course-play">
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-white text-[#1c1d1f] flex items-center justify-center shadow-lg"><Play className="w-6 h-6 ml-0.5" /></span>
        </span>
        <span className="absolute bottom-3 inset-x-0 text-center text-sm font-semibold">Preview this course</span>
      </button>
    );
  }
  if (page.video_kind === "mp4") {
    return <video className="w-full aspect-video bg-black" src={page.video_url} controls autoPlay playsInline />;
  }
  const src = page.video_kind === "vimeo"
    ? `https://player.vimeo.com/video/${page.video_embed_id}?autoplay=1`
    : `https://www.youtube.com/embed/${page.video_embed_id}?autoplay=1`;
  return <iframe title={page.title} className="w-full aspect-video" src={src} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />;
}

export default function StorefrontCoursePage({ config }) {
  const { slug, base } = useStoreBase();
  const { courseSlug } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState({});
  const [allOpen, setAllOpen] = useState(false);
  const [more, setMore] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!config?.course_page_maker_active || !slug || !courseSlug) return;
    setMissing(false);
    setPage(null);
    api.get(`/store/${slug}/course/${courseSlug}`).then(({ data }) => setPage(data)).catch(() => setMissing(true));
  }, [config?.course_page_maker_active, slug, courseSlug]);

  const lectureTotal = useMemo(() => (page?.modules || []).reduce((n, m) => n + (m.lectures || []).length, 0), [page]);

  if (!config?.course_page_maker_active) return <Navigate to={`${base}/`} replace />;
  if (missing) return <Navigate to={`${base}/`} replace />;
  if (!page) return <div className="max-w-6xl mx-auto px-4 py-16 text-slate-500">Loading course…</div>;

  const storeName = config?.tenant?.name || "Store";
  const desc = page.description || "";
  const descShort = desc.length > 520 && !more ? `${desc.slice(0, 520)}…` : desc;
  const toggleAll = () => {
    const next = !allOpen;
    setAllOpen(next);
    const map = {};
    (page.modules || []).forEach((_, i) => { map[i] = next; });
    setOpen(map);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post(`/store/${slug}/course/${courseSlug}/lead`, form);
      setDone(data.message || "Thank you. We have received your details.");
    } catch (ex) {
      setErr(extractErr(ex));
    } finally { setSending(false); }
  };

  const renderCard = () => (
    <div className="bg-white text-slate-900 shadow-xl border border-slate-200 overflow-hidden md:sticky md:top-4">
      <VideoPreview page={page} />
      <div className="p-4 sm:p-5 space-y-3">
        <div className="text-2xl sm:text-3xl font-bold" data-testid="course-price">{money(page.price)}</div>
        {(page.taglines || []).length > 0 && (
          <ul className="space-y-1.5 text-sm text-slate-700">
            {page.taglines.map((line) => (
              <li key={line} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" /> {line}</li>
            ))}
          </ul>
        )}
        {done ? (
          <div className="rounded-md bg-emerald-50 text-emerald-800 px-3 py-3 text-sm" data-testid="course-enquiry-done">{done}</div>
        ) : (
          <form className="space-y-2" onSubmit={submit} data-testid="course-enquiry-form">
            <input required className="w-full h-11 rounded-md border border-slate-300 px-3 text-base" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="course-enquiry-name" />
            <input required className="w-full h-11 rounded-md border border-slate-300 px-3 text-base" placeholder="Phone number" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="course-enquiry-phone" />
            <input required type="email" className="w-full h-11 rounded-md border border-slate-300 px-3 text-base" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="course-enquiry-email" />
            {err && <p className="text-sm text-rose-600">{err}</p>}
            <button type="submit" disabled={sending} className="w-full h-12 rounded-md bg-[#a435f0] hover:bg-[#8710d8] text-white font-bold text-base" data-testid="course-enquiry-send">
              {sending ? "Sending…" : "Send enquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="course-page" className="bg-white text-slate-900 pb-16">
      <div className="md:hidden">{renderCard()}</div>
      <section className="bg-[#1c1d1f] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-10 md:pr-[340px]">
          <div className="text-xs sm:text-sm text-violet-200 flex flex-wrap gap-x-2 gap-y-1">
            <Link to={base || "/"} className="hover:underline">{storeName}</Link>
            <span>›</span>
            <span className="text-white/80">Course</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3 leading-tight" data-testid="course-hero">{page.title}</h1>
          {page.short_description && <p className="mt-3 text-base sm:text-lg text-white/90 max-w-3xl">{page.short_description}</p>}
          {(page.tags || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {page.tags.map((tag) => {
                const hot = tag === "Best Seller";
                const rated = tag === "Highest Rated";
                const cls = hot ? "bg-[#c0e1c2] text-[#0f5132]" : rated ? "bg-[#f3ca8c] text-[#6b4b16]" : "bg-white/10 text-white border border-white/20";
                return <span key={tag} className={`text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-sm ${cls}`}>{tag}</span>;
              })}
            </div>
          )}
          <div className="mt-4 text-sm text-white/80 space-y-1">
            {page.author_name && <div>Created by <span className="text-violet-300 font-semibold">{page.author_name}</span></div>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {Number(page.rating_avg) > 0 && (
                <span className="inline-flex items-center gap-1.5 text-amber-300 font-bold">
                  {Number(page.rating_avg).toFixed(1)} <Stars value={page.rating_avg} />
                  <span className="text-white/70 font-normal">{Number(page.rating_count || 0).toLocaleString("en-IN")} ratings</span>
                </span>
              )}
              {page.language && <span className="inline-flex items-center gap-1"><Globe className="w-4 h-4" /> {page.language}</span>}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:grid md:grid-cols-[minmax(0,1fr)_320px] md:gap-8">
        <div className="space-y-8 min-w-0">
          {(page.learn_items || []).length > 0 && (
            <section className="border border-slate-200 p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold">{page.learn_title || "What you'll learn"}</h2>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {page.learn_items.map((item) => {
                  const Icon = ICONS[item.icon] || Check;
                  return (
                    <li key={`${item.icon}-${item.text}`} className="flex items-start gap-2 text-sm">
                      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-slate-700" />
                      <span>{item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {(page.modules || []).length > 0 && (
            <section>
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">Course content</h2>
                  <p className="text-sm text-slate-500 mt-1">{page.modules.length} sections • {lectureTotal} lectures</p>
                </div>
                <button type="button" className="text-sm font-semibold text-[#6d28d9]" onClick={toggleAll}>{allOpen ? "Collapse all sections" : "Expand all sections"}</button>
              </div>
              <div className="mt-3 border border-slate-200 divide-y">
                {page.modules.map((mod, i) => {
                  const shown = Boolean(open[i]);
                  return (
                    <div key={`${mod.title}-${i}`}>
                      <button type="button" className="w-full flex items-center gap-3 px-3 sm:px-4 py-3 text-left bg-slate-50 hover:bg-slate-100" onClick={() => setOpen((s) => ({ ...s, [i]: !s[i] }))}>
                        <ChevronDown className={`w-4 h-4 shrink-0 transition ${shown ? "rotate-180" : ""}`} />
                        <span className="font-semibold flex-1">{mod.title}</span>
                        <span className="text-xs sm:text-sm text-slate-500 whitespace-nowrap">{(mod.lectures || []).length} lectures{mod.length_label ? ` • ${mod.length_label}` : ""}</span>
                      </button>
                      {shown && (
                        <ul className="bg-white">
                          {(mod.lectures || []).map((lec) => (
                            <li key={lec.title} className="flex items-center justify-between gap-3 px-4 sm:px-10 py-2.5 text-sm border-t border-slate-100">
                              <span className="inline-flex items-center gap-2 min-w-0"><Play className="w-3.5 h-3.5 shrink-0 text-slate-400" /> <span className="truncate">{lec.title}</span></span>
                              {lec.duration && <span className="text-slate-500 shrink-0">{lec.duration}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {desc && (
            <section>
              <h2 className="text-xl sm:text-2xl font-bold">Description</h2>
              <p className="mt-3 text-sm sm:text-base leading-7 whitespace-pre-wrap text-slate-700">{descShort}</p>
              {desc.length > 520 && (
                <button type="button" className="mt-2 text-sm font-semibold text-[#6d28d9]" onClick={() => setMore((v) => !v)}>{more ? "Show less" : "Show more"}</button>
              )}
            </section>
          )}
        </div>
        <aside className="hidden md:block md:-mt-64 relative z-10">{renderCard()}</aside>
      </div>
    </div>
  );
}
